import { useCallback, useEffect, useState } from "react";

const clampValue = (min, value, max) => {
  return Math.min(Math.max(value, min), max);
};

const useResize = ({
  axis,
  ref,
  minSize,
  maxSize,
  storageKey,
  reverse = false,
}) => {
  const clientAxis = axis.toLowerCase() === "x" ? "clientX" : "clientY";

  const savedSize = parseInt(localStorage.getItem(storageKey), 10);

  const [size, setSize] = useState(savedSize || null);
  const [resizing, setResizing] = useState(false);

  const handleResize = useCallback(
    (event) => {
      const isTouch = event.hasOwnProperty("touches");
      let lastPoint = isTouch
        ? event.touches[0][clientAxis]
        : event[clientAxis];

      const onMove = (e) => {
        const clientPoint = isTouch ? e.touches[0][clientAxis] : e[clientAxis];
        const delta = (clientPoint - lastPoint) * (reverse ? -1 : 1);
        lastPoint = clientPoint;

        setSize((currentSize) => {
          if (currentSize === null) {
            const currentSize =
              axis.toLowerCase() === "x"
                ? ref.current.clientWidth
                : ref.current.clientHeight;
            return clampValue(minSize, currentSize - delta, maxSize);
          }
          return clampValue(minSize, currentSize - delta, maxSize);
        });
      };
      const onEnd = () => {
        setResizing(false);
        document.body.classList.remove("select-none");
        document.removeEventListener(
          isTouch ? "touchmove" : "mousemove",
          onMove
        );
        document.removeEventListener(isTouch ? "touchend" : "mouseup", onEnd);
      };
      setResizing(true);
      document.body.classList.add("select-none");
      document.addEventListener(isTouch ? "touchmove" : "mousemove", onMove);
      document.addEventListener(isTouch ? "touchend" : "mouseup", onEnd);
    },
    [axis, clientAxis, ref, minSize, maxSize, reverse]
  );
  const handleReset = useCallback(() => {
    setResizing(false);
    setSize(null);
  }, []);

  useEffect(() => {
    if (resizing === false) {
      if (size === null) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, clampValue(minSize, size, maxSize));
      }
    }
  }, [resizing, size, storageKey, minSize, maxSize]);

  return {
    size,
    resizing,
    handleResize,
    handleReset,
  };
};

export default useResize;
